/**
 * parcoursNavigationEngine.js
 *
 * Moteur de navigation du parcours guide.
 * Determine la prochaine activite a faire en fonction de la progression.
 *
 * Tous les chapitres sont lineaires (liste ordonnee de steps).
 * La structure est chargee depuis Firestore (v3) ou fallback hardcode.
 *
 * Supporte 14 types de steps : discovery, rhythm-group, quiz, drill,
 * memorization, combo, roulette, lecture, breaks, chrono, metronome,
 * metronome-challenge, reproduire, drumless.
 */

import { isExerciseCompleted } from './discoveryStorage';
import {
  getParcoursProgression,
  isStepCompleteInStorage,
  getFailedStepIds,
  isRhythmCompleteInParcours,
  isStepAttempted
} from './parcoursProgressionStorage';
import { getChapitres, getChapitreConfig } from './parcoursConfigLoader';

// ============================================================
// API publique
// ============================================================

/**
 * Calcule l'etat complet du parcours : chapitre courante, steps avec statuts.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {Object} { currentChapitre, steps[], currentStepIndex, chapitreProgress }
 */
export function computeParcoursState(exercises, chapitreOverride = null) {
  const progression = getParcoursProgression();
  const chapitre = chapitreOverride || progression.currentChapitre || 1;
  const chapitreConfig = getChapitreConfig(chapitre);

  if (!chapitreConfig) {
    return { currentChapitre: chapitre, steps: [], currentStepIndex: -1, chapitreProgress: null };
  }

  return computeLinearChapitreState(chapitre, chapitreConfig, exercises, progression);
}

/**
 * Determine la prochaine activite a lancer.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @param {string|null} currentStepId - stepId du step qu'on vient de compléter (ex: 'p1-11q')
 *   Si fourni, la navigation continue dans le chapitre du step (pas currentChapitre).
 *   Indispensable quand l'user est au chapitre 15 mais complète un step du chapitre 1.
 * @returns {Object} { path, stepId, label, fromParcours: true, subActivityId?, chapitreComplete? }
 */
export function getNextParcoursActivity(exercises, currentStepId = null) {
  // Dériver le chapitreOverride depuis le stepId (ex: 'p1-11q' → 1)
  let chapitreOverride = null;
  if (currentStepId) {
    const match = currentStepId.match(/^p(\d+)-/);
    if (match) chapitreOverride = parseInt(match[1]);
  }

  const state = computeParcoursState(exercises, chapitreOverride);

  if (state.currentChapitre <= 0) {
    return { path: '/parcours', chapitreComplete: true };
  }

  return getNextLinearActivity(state, exercises);
}

/**
 * Resout l'activite pour un step donne (par son objet step).
 * Utile pour relancer un step deja complete.
 *
 * @param {Object} step - l'objet step (avec id, type, params, etc.)
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {Object} { path, stepId, label, fromParcours: true, ... }
 */
export function resolveStepActivity(step, exercises) {
  const state = computeParcoursState(exercises);
  return getActivityForStep(step, exercises, state.currentChapitre);
}

/**
 * Trouve l'exercice suivant dans une rhythm-group du parcours.
 * Respecte la orderRange du step courant.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @param {string} currentExerciseId - l'exercice qu'on vient de finir
 * @param {number[]} orderRange - [min, max] du step
 * @returns {Object|null} { id, path } du prochain exercice, ou null si range terminee
 */
export function getNextRhythmInRange(exercises, currentExerciseId, orderRange) {
  const [min, max] = orderRange;
  const rhythmsInRange = Object.values(exercises)
    .filter(ex => ex.categoryId === 'rhythms' && ex.type === 'standard' && ex.order >= min && ex.order <= max)
    .sort((a, b) => a.order - b.order);

  const currentIdx = rhythmsInRange.findIndex(ex => ex.id === currentExerciseId);

  // Prochain dans la range (navigation séquentielle)
  if (currentIdx >= 0 && currentIdx < rhythmsInRange.length - 1) {
    const next = rhythmsInRange[currentIdx + 1];
    return { id: next.id, path: `/exercise/${next.id}` };
  }

  return null; // Range terminee
}

/**
 * Retourne l'ensemble des types de steps débloqués dans la sidebar,
 * en s'appuyant sur la progression multi-chapitres.
 *
 * Logique :
 * - Chapitres < currentChapitre → toutes les étapes sont terminées (l'utilisateur ne
 *   peut pas avancer sans compléter le chapitre entière).
 * - Chapitre courante → seules les étapes dont status === 'completed' comptent.
 *
 * Pour les steps de type 'combo', les types des sous-activités sont également ajoutés.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {Set<string>} ensemble des types débloqués (ex: 'discovery', 'rhythm-group', ...)
 */
export function getUnlockedSidebarCategories(exercises) {
  const progression = getParcoursProgression();
  const currentChapitre = progression.currentChapitre || 1;
  const chapitres = getChapitres();

  if (!chapitres) return new Set();

  const unlockedTypes = new Set();

  Object.entries(chapitres).forEach(([chapitreNumStr, chapitreConfig]) => {
    const chapitreNum = parseInt(chapitreNumStr, 10);
    const steps = chapitreConfig?.steps || [];

    if (chapitreNum < currentChapitre) {
      // Chapitre terminée : tous les steps sont considérés accomplis
      steps.forEach(step => _collectStepTypes(step, unlockedTypes));
    } else if (chapitreNum === currentChapitre) {
      // Chapitre courante : seuls les steps complétés
      const state = computeParcoursState(exercises, chapitreNum);
      state.steps.forEach(step => {
        if (step.status === 'completed') {
          _collectStepTypes(step, unlockedTypes);
        }
      });
    }
    // Chapitres futures : ignorées
  });

  return unlockedTypes;
}

/** Ajoute le type du step (et les sous-types si combo) dans l'ensemble fourni. */
function _collectStepTypes(step, typesSet) {
  typesSet.add(step.type);
  // Pour les combos : débloquer aussi les types des sous-activités
  const activities = step.params?.activities || step.activities || [];
  activities.forEach(act => { if (act?.type) typesSet.add(act.type); });
}

/**
 * Retourne l'ensemble des noms de drills débloqués via le parcours (noms normalisés).
 *
 * Un drill est débloqué dès que le step parcours correspondant est complété.
 * La normalisation est identique à normalizeName() : lowercase, sans espaces/tirets/underscores.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {Set<string>} noms normalisés des drills débloqués
 */
export function getUnlockedDrillNames(exercises) {
  const progression = getParcoursProgression();
  const currentChapitre = progression.currentChapitre || 1;
  const chapitres = getChapitres();

  if (!chapitres) return new Set();

  const unlockedNames = new Set();

  const _collectDrillName = (step) => {
    const drillName = step.params?.drillName ?? step.drillName;
    if (drillName) {
      unlockedNames.add(normalizeName(drillName));
      // Drill réellement joué (peut être un repli si le drill nommé n'existe pas)
      const resolved = findDrillByName(exercises, drillName, step.id);
      if (resolved?.name) unlockedNames.add(normalizeName(resolved.name));
    }
    // Sous-activités drill dans un combo
    const activities = step.params?.activities || step.activities || [];
    activities.forEach(act => {
      if (act?.type === 'drill') {
        const name = act.params?.drillName ?? act.drillName;
        if (name) unlockedNames.add(normalizeName(name));
      }
    });
  };

  Object.entries(chapitres).forEach(([chapitreNumStr, chapitreConfig]) => {
    const chapitreNum = parseInt(chapitreNumStr, 10);
    const steps = chapitreConfig?.steps || [];

    if (chapitreNum < currentChapitre) {
      steps.filter(s => s.type === 'drill' || s.type === 'combo').forEach(_collectDrillName);
    } else if (chapitreNum === currentChapitre) {
      const state = computeParcoursState(exercises, chapitreNum);
      state.steps.forEach(step => {
        if (step.status === 'completed' && (step.type === 'drill' || step.type === 'combo')) {
          _collectDrillName(step);
        }
      });
    }
  });

  return unlockedNames;
}

/**
 * Retourne le tier maximum de quiz débloqué via le parcours.
 *
 * Logique identique à getUnlockedSidebarCategories :
 * - Chapitres terminées (< currentChapitre) : tous leurs steps quiz comptent.
 * - Chapitre courante : seuls les steps quiz avec status 'completed' comptent.
 * - Les sous-activités quiz dans les combos sont également prises en compte.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {number} tier max débloqué (-1 si aucun quiz complété)
 */
export function getMaxUnlockedQuizTier(exercises) {
  const progression = getParcoursProgression();
  const currentChapitre = progression.currentChapitre || 1;
  const chapitres = getChapitres();

  if (!chapitres) return -1;

  let maxTier = -1;

  const _extractQuizTier = (step) => {
    const tier = step.params?.tier ?? step.tier;
    if (tier !== undefined && tier > maxTier) maxTier = tier;
  };

  const _processStep = (step) => {
    if (step.type === 'quiz') {
      _extractQuizTier(step);
    }
    // Sous-activités quiz dans un combo
    const activities = step.params?.activities || step.activities || [];
    activities.forEach(act => {
      if (act?.type === 'quiz') {
        const tier = act.params?.tier ?? act.tier;
        if (tier !== undefined && tier > maxTier) maxTier = tier;
      }
    });
  };

  Object.entries(chapitres).forEach(([chapitreNumStr, chapitreConfig]) => {
    const chapitreNum = parseInt(chapitreNumStr, 10);
    const steps = chapitreConfig?.steps || [];

    if (chapitreNum < currentChapitre) {
      steps.forEach(_processStep);
    } else if (chapitreNum === currentChapitre) {
      const state = computeParcoursState(exercises, chapitreNum);
      state.steps.forEach(step => {
        if (step.status === 'completed') _processStep(step);
      });
    }
  });

  return maxTier;
}

/**
 * Retourne le groupe maximum de quiz-lecture débloqué via le parcours.
 *
 * - Chapitres terminées (< currentChapitre) : tous leurs steps quiz-lecture comptent.
 * - Chapitre courante : seuls les steps quiz-lecture avec status 'completed' comptent.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @returns {number} groupId max débloqué (-1 si aucun quiz-lecture complété)
 */
export function getMaxUnlockedQuizLectureGroup(exercises) {
  const progression = getParcoursProgression();
  const currentChapitre = progression.currentChapitre || 1;
  const chapitres = getChapitres();

  if (!chapitres) return -1;

  let maxGroup = -1;

  const _processStep = (step) => {
    if (step.type === 'quiz-lecture') {
      const groupId = step.params?.groupId ?? step.groupId;
      if (groupId !== undefined && groupId > maxGroup) maxGroup = groupId;
    }
  };

  Object.entries(chapitres).forEach(([chapitreNumStr, chapitreConfig]) => {
    const chapitreNum = parseInt(chapitreNumStr, 10);
    const steps = chapitreConfig?.steps || [];

    if (chapitreNum < currentChapitre) {
      steps.forEach(_processStep);
    } else if (chapitreNum === currentChapitre) {
      const state = computeParcoursState(exercises, chapitreNum);
      state.steps.forEach(step => {
        if (step.status === 'completed') _processStep(step);
      });
    }
  });

  return maxGroup;
}

/**
 * Normalise un nom pour comparaison flexible :
 * 'DRILL_CROCHES_1' → 'drillcroches1'
 * 'Drill Croches 1' → 'drillcroches1'
 * 'drill-croches-1' → 'drillcroches1'
 */
function normalizeName(str) {
  return (str || '').toLowerCase().replace(/[_\s\-]/g, '');
}

/**
 * Trouve un drill par son nom dans les exercices Firestore.
 * Essaie d'abord un match exact, puis un match normalise (insensible a la casse,
 * underscores/espaces/tirets ignores).
 * Si le drill n'existe pas (ex: DRILL8 à DRILL15 prévus par la config mais jamais créés),
 * repli sur un drill disponible, choisi par rotation selon le numéro de chapitre du step
 * (ex: 'p36-5' → chapitre 36) pour que les chapitres successifs tournent sur tous les drills.
 *
 * @param {Object} exercises - exercices de ExercisesContext
 * @param {string} drillName - nom du drill (ex: 'DRILL_CROCHES_1')
 * @param {string} [stepId] - id du step (ex: 'p36-5'), sert de clé de rotation pour le repli
 * @returns {Object|null} exercice drill ou null
 */
export function findDrillByName(exercises, drillName, stepId) {
  if (!exercises || !drillName) return null;
  const drills = Object.values(exercises).filter(ex => ex.type === 'drill');

  // 1. Match exact
  const exact = drills.find(ex => ex.name === drillName);
  if (exact) return exact;

  // 2. Match normalise (DRILL_CROCHES_1 == Drill Croches 1)
  const normalized = normalizeName(drillName);
  const fuzzy = drills.find(ex => normalizeName(ex.name) === normalized);
  if (fuzzy) return fuzzy;

  // 3. Repli : drill disponible, par rotation sur le numéro de chapitre
  if (drills.length === 0) return null;
  const sorted = [...drills].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const chapitreNum = parseInt(String(stepId || '').match(/^p(\d+)-/)?.[1], 10) || 0;
  const fallback = sorted[chapitreNum % sorted.length];
  console.warn(`[ParcoursNav] Drill "${drillName}" inexistant, repli sur "${fallback.name}" (step ${stepId})`);
  return fallback;
}

// ============================================================
// Calcul de l'etat lineaire (tous les chapitres)
// ============================================================

function computeLinearChapitreState(chapitre, chapitreConfig, exercises, progression) {
  const rawSteps = chapitreConfig.steps || [];

  // Calcul du statut de completion et d'acces (done = tenté ou complété)
  // pour chaque step, utilisé pour le verrouillage séquentiel.
  const stepComputed = rawSteps.map(step => {
    const isCompleted = getStepStatus(step, exercises, progression) === 'completed';
    // Un step est "fait" s'il a été complété (même en échec pour les non-dérivables)
    // OU s'il a été tenté (pour les steps dérivables : discovery, rhythm-group).
    const isDone = isCompleted || isStepAttempted(step.id);
    return { isCompleted, isDone };
  });

  // Verrouillage séquentiel : step N verrouillé si step N-1 n'a pas été fait.
  // L'utilisateur doit impérativement réaliser le step N avant d'accéder au step N+1.
  const steps = rawSteps.map((step, index) => {
    const { isCompleted } = stepComputed[index];
    const isLocked = index > 0 && !stepComputed[index - 1].isDone;

    // Un step déjà complété n'est jamais verrouillé (ex: step précédent ajouté après coup à la config)
    let status;
    if (isCompleted) {
      status = 'completed';
    } else if (isLocked) {
      status = 'locked';
    } else {
      status = 'pending';
    }

    return {
      ...step,
      status,
      progress: getStepProgress(step, exercises)
    };
  });

  // Step courant = premier step accessible (non locked) et non complété
  let currentStepIndex = steps.findIndex(s => s.status === 'pending');
  if (currentStepIndex === -1) currentStepIndex = steps.length; // Tous les steps accessibles sont complétés

  // Marquer le step courant comme 'current'
  if (currentStepIndex < steps.length && steps[currentStepIndex].status === 'pending') {
    steps[currentStepIndex].status = 'current';
  }

  const completedCount = steps.filter(s => s.status === 'completed').length;

  return {
    currentChapitre: chapitre,
    steps,
    currentStepIndex,
    chapitreProgress: {
      completedSteps: completedCount,
      totalSteps: steps.length,
      isChapitreComplete: steps.length > 0 && completedCount === steps.length
    }
  };
}

function getNextLinearActivity(state, exercises) {
  const { steps, currentStepIndex, currentChapitre } = state;

  if (currentStepIndex < 0 || currentStepIndex >= steps.length) {
    // Tous les steps sont completes — verifier s'il reste des echecs a rattraper
    const failedIds = getFailedStepIds();
    if (failedIds.length > 0) {
      // Trouver le premier step echoue et le renvoyer en mode retry
      const firstFailedKey = failedIds[0];
      // Le stepId peut etre "p1-3" ou "p1-3-quiz" (combo)
      const failedStep = steps.find(s => firstFailedKey === s.id || firstFailedKey.startsWith(s.id + '-'));
      if (failedStep) {
        const subActivityId = firstFailedKey.length > failedStep.id.length
          ? firstFailedKey.slice(failedStep.id.length + 1)
          : null;
        const activity = getActivityForStep(failedStep, exercises, currentChapitre);
        console.log('[ParcoursDebug][Engine] chapitre', currentChapitre, 'steps completes mais retry propose', {
          failedIds, firstFailedKey, retryStepId: failedStep.id, subActivityId
        });
        return {
          ...activity,
          stepId: failedStep.id,
          subActivityId: subActivityId || null,
          isParcoursRetry: true,
          remainingRetries: failedIds.length,
        };
      }
      // failedIds non vide mais aucun ne correspond aux steps de CE chapitre
      // (steps d'un autre chapitre) — chapitre quand meme considere termine ici.
      console.warn('[ParcoursDebug][Engine] failedIds presents mais hors chapitre courant', {
        currentChapitre, failedIds, stepsIds: steps.map(s => s.id)
      });
    }
    // Chapitre vraiment terminee
    console.log('[ParcoursDebug][Engine] chapitre', currentChapitre, 'complete →', currentChapitre + 1);
    return { path: '/parcours', chapitreComplete: true, nextChapitre: currentChapitre + 1 };
  }

  const step = steps[currentStepIndex];
  return getActivityForStep(step, exercises, currentChapitre);
}

// ============================================================
// Résolution d'activité par type de step
// ============================================================

/**
 * Lit un paramètre depuis step.params (v3) ou directement depuis step (v2 fallback).
 */
function getParam(step, key) {
  return step.params?.[key] ?? step[key];
}

function getActivityForStep(step, exercises, currentChapitre) {
  switch (step.type) {
    case 'discovery': {
      const exerciseId = getParam(step, 'exerciseId');
      // Toujours convertir en number : Firestore peut stocker comme string "3"
      const stageRaw = getParam(step, 'stage');
      const stage = stageRaw ? parseInt(stageRaw, 10) : null;
      const questionsPerStageRaw = getParam(step, 'questionsPerStage');
      const questionsPerStage = questionsPerStageRaw ? parseInt(questionsPerStageRaw, 10) : null;
      // Auto-dériver le path depuis exerciseId (rétrocompatible : utilise path stocké si présent)
      let path = getParam(step, 'path');
      if (!path) {
        if (!exerciseId || exerciseId === 'preparation-batterie') path = '/decouverte/preparation';
        else path = `/decouverte/${exerciseId}`;
      }
      return {
        path,
        stepId: step.id,
        label: step.label,
        fromParcours: true,
        stage: stage || null,
        questionsPerStage: questionsPerStage || null
      };
    }

    case 'rhythm-group': {
      const orderRange = getParam(step, 'orderRange');
      const rhythms = getRhythmsInRange(exercises, orderRange);
      const firstIncomplete = findFirstIncompleteRhythmInParcours(rhythms, step.id);
      // Si tous les rythmes sont complets, revenir au premier pour permettre de refaire
      const target = firstIncomplete || rhythms[0];
      if (target) {
        return {
          path: `/exercise/${target.id}`,
          stepId: step.id,
          label: step.label,
          fromParcours: true,
          orderRange
        };
      }
      return { path: '/parcours', stepId: step.id, label: step.label, fromParcours: true };
    }

    case 'quiz':
      return {
        path: getParam(step, 'path') || `/quiz/${getParam(step, 'tier')}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    case 'drill': {
      const drillName = getParam(step, 'drillName');
      const drill = findDrillByName(exercises, drillName, step.id);
      if (drill) {
        return {
          path: `/exercise/${drill.id}`,
          stepId: step.id,
          label: step.label,
          fromParcours: true
        };
      }
      console.warn(`[ParcoursNav] Drill "${drillName}" non trouve dans Firestore`);
      return { path: '/parcours', stepId: step.id, label: step.label, fromParcours: true };
    }

    case 'memorization':
      return {
        path: getParam(step, 'path') || `/ecoute-reproduit/memorisation/${getParam(step, 'tier')}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    case 'combo': {
      const activities = getParam(step, 'activities') || [];
      for (const activity of activities) {
        if (!isStepCompleteInStorage(step.id, activity.id)) {
          const activityResult = getActivityForStep(
            { ...activity, id: step.id, params: activity.params || activity },
            exercises,
            currentChapitre
          );
          return {
            ...activityResult,
            stepId: step.id,
            subActivityId: activity.id,
            label: activity.label || step.label
          };
        }
      }
      return { path: '/parcours', stepId: step.id, label: step.label, fromParcours: true };
    }

    // === Nouveaux types ===

    case 'roulette':
      return {
        path: `/roulette/${getParam(step, 'tier')}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    case 'lecture': {
      const groupId = getParam(step, 'groupId');

      // Backward compat : ancien format avec levelId direct
      if (!groupId) {
        return {
          path: `/lecture/play/${getParam(step, 'levelId')}`,
          stepId: step.id,
          label: step.label,
          fromParcours: true
        };
      }

      // Nouveau format : enchaînement automatique A (1 mes.) → B (2 mes.) → C (4 mes.)
      const baseLevel = (parseInt(groupId) - 1) * 3;
      const chapitreADone = isStepCompleteInStorage(step.id, 'A');
      const chapitreBDone = isStepCompleteInStorage(step.id, 'B');

      let levelId, chapitre;
      if (!chapitreADone) {
        levelId = baseLevel + 1;
        chapitre = 'A';
      } else if (!chapitreBDone) {
        levelId = baseLevel + 2;
        chapitre = 'B';
      } else {
        levelId = baseLevel + 3;
        chapitre = 'C';
      }

      return {
        path: `/lecture/play/${levelId}`,
        stepId: step.id,
        subActivityId: chapitre, // transmis comme parcoursSubActivity → markStepComplete l'utilise
        label: step.label,
        fromParcours: true
      };
    }

    case 'breaks': {
      // Nouveau format : groupId + withGroove → calcul du levelId
      const groupId = getParam(step, 'groupId');
      const withGroove = getParam(step, 'withGroove');
      const breaksLevelId = groupId
        ? (groupId - 1) * 2 + (withGroove ? 2 : 1)
        : getParam(step, 'levelId'); // Rétrocompatibilité ancien format
      return {
        path: `/breaks/play/${breaksLevelId}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };
    }

    case 'chrono': {
      const exerciseId = getParam(step, 'exerciseId');
      return {
        path: `/exercise/${exerciseId}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };
    }

    case 'metronome': {
      const exerciseId = getParam(step, 'exerciseId');
      return {
        path: `/exercise/${exerciseId}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };
    }

    case 'metronome-challenge': {
      const tierId = getParam(step, 'tierId');
      const seed = getParam(step, 'seed');
      const count = parseInt(getParam(step, 'count')) || 1;
      // Le count est encodé dans l'URL (?count=N) pour être disponible sans dépendre du cache Firestore
      const countSuffix = count > 1 ? `?count=${count}` : '';
      return {
        path: seed
          ? `/metronome-challenge/${tierId}/${seed}${countSuffix}`
          : `/metronome-challenge/${tierId}${countSuffix ? `?${countSuffix.slice(1)}` : ''}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true,
        count
      };
    }

    case 'reproduire':
      return {
        path: '/ecoute-reproduit/reproduire-rythmes',
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    case 'drumless':
      return {
        path: `/drumless/play/${getParam(step, 'songId')}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    case 'quiz-lecture':
      return {
        path: `/quiz-lecture/${getParam(step, 'groupId')}`,
        stepId: step.id,
        label: step.label,
        fromParcours: true
      };

    default:
      return { path: '/parcours', stepId: step.id, label: step.label, fromParcours: true };
  }
}

// ============================================================
// Statut et progression des steps
// ============================================================

/**
 * Determine le statut d'un step : 'completed' ou 'pending'
 */
function getStepStatus(step, exercises, progression) {
  switch (step.type) {
    case 'discovery': {
      const exerciseId = getParam(step, 'exerciseId');
      if (!exerciseId || exerciseId === 'preparation-batterie') {
        // Dérivable depuis discoveryStorage (markStepComplete aussi appelé, les deux marchent)
        return isExerciseCompleted(exerciseId) ? 'completed' : 'pending';
      }
      // Steps avec stage : tracking parcours-spécifique UNIQUEMENT.
      // Évite que l'accès libre ne marque un step avec stage comme complété.
      const stageRaw = getParam(step, 'stage');
      if (stageRaw) {
        return isStepCompleteInStorage(step.id) ? 'completed' : 'pending';
      }
      // Découverte rythmique répétée à chaque chapitre (p{N}-disc9, même exercice rythmique-9 mais
      // difficulté croissante) : tracking par step uniquement, sinon un seul passage validerait tous les chapitres.
      if (/-disc9$/.test(step.id)) {
        return isStepCompleteInStorage(step.id) ? 'completed' : 'pending';
      }
      // Steps sans stage (rythmique-X) : fallback sur discoveryStorage.
      // Rattrapage des anciens parcours où markStepComplete n'était pas appelé (bug 48b4d64).
      if (isStepCompleteInStorage(step.id) || isExerciseCompleted(exerciseId)) return 'completed';
      return 'pending';
    }

    case 'rhythm-group': {
      const orderRange = getParam(step, 'orderRange');
      const rhythms = getRhythmsInRange(exercises, orderRange);
      if (rhythms.length === 0) return 'pending';
      // Tous les rythmes validés (≥85%) → completed
      if (rhythms.every(r => isRhythmCompleteInParcours(step.id, r.id))) return 'completed';
      // Range traversée entièrement (même avec des échecs) → markStepComplete a été appelé → completed
      if (isStepCompleteInStorage(step.id)) return 'completed';
      return 'pending';
    }

    case 'drill': {
      return isStepCompleteInStorage(step.id) ? 'completed' : 'pending';
    }

    case 'combo': {
      const activities = getParam(step, 'activities') || [];
      return activities.every(a => isStepCompleteInStorage(step.id, a.id))
        ? 'completed' : 'pending';
    }

    case 'lecture': {
      const groupId = getParam(step, 'groupId');
      if (!groupId) {
        return isStepCompleteInStorage(step.id) ? 'completed' : 'pending';
      }
      return (isStepCompleteInStorage(step.id, 'A') &&
              isStepCompleteInStorage(step.id, 'B') &&
              isStepCompleteInStorage(step.id, 'C')) ? 'completed' : 'pending';
    }

    // Tous les types non-derivables
    case 'quiz':
    case 'quiz-lecture':
    case 'memorization':
    case 'roulette':
    case 'breaks':
    case 'chrono':
    case 'metronome':
    case 'metronome-challenge':
    case 'reproduire':
    case 'drumless': {
      return isStepCompleteInStorage(step.id) ? 'completed' : 'pending';
    }

    default:
      return 'pending';
  }
}

/**
 * Calcule la progression d'un step (pour affichage).
 */
function getStepProgress(step, exercises) {
  if (step.type === 'lecture' && getParam(step, 'groupId')) {
    const completed = ['A', 'B', 'C'].filter(p => isStepCompleteInStorage(step.id, p)).length;
    return { completed, total: 3 };
  }

  if (step.type === 'rhythm-group') {
    const orderRange = getParam(step, 'orderRange');
    const rhythms = getRhythmsInRange(exercises, orderRange);
    const completed = rhythms.filter(r => isRhythmCompleteInParcours(step.id, r.id)).length;
    return { completed, total: rhythms.length };
  }

  if (step.type === 'combo') {
    const activities = getParam(step, 'activities') || [];
    const completed = activities.filter(
      a => isStepCompleteInStorage(step.id, a.id)
    ).length;
    return { completed, total: activities.length };
  }

  return null;
}

// ============================================================
// Helpers
// ============================================================

/**
 * Filtre les exercices de type rhythms dans une orderRange.
 */
function getRhythmsInRange(exercises, orderRange) {
  if (!exercises || !orderRange) return [];
  const [min, max] = orderRange;
  return Object.values(exercises)
    .filter(ex => ex.categoryId === 'rhythms' && ex.type === 'standard' && ex.order >= min && ex.order <= max)
    .sort((a, b) => a.order - b.order);
}

/**
 * Trouve le premier rythme non complété dans le contexte du parcours.
 * Utilise le tracking parcours-specific (indépendant de l'accès libre).
 */
function findFirstIncompleteRhythmInParcours(rhythms, stepId) {
  return rhythms.find(r => !isRhythmCompleteInParcours(stepId, r.id)) || null;
}
